<template>
  <div class="flex h-full min-h-0 flex-col overflow-hidden">
    <div
      class="source-content file-dropzone h-full min-h-0"
      @dragover.prevent.stop="startDragover"
    >
      <div
        v-if="isDragging"
        @drop.prevent="handleDrop"
        @dragleave="isDragging = false"
        class="file-dropzone__droparea absolute inset-0 z-[100] flex items-center justify-center rounded-[3px] border border-dashed border-white/100 bg-black/50"
      >
        <div class="max-w-[80px] text-[24px] font-bold text-white">Drag + drop image or video to upload</div>
      </div>

      <div class="filter-bar flex items-center gap-4">
        <search-filter
          class="flex-grow"
          :value="assetSearch"
          searchOnly
          data-test="producer-designLibrary-searchFilter"
          @update:value="searchAssets"
        />
        <div class="design-library__filter-wrap">
          <button
            class="border-0 bg-transparent p-0 text-[13px] font-medium text-[#a5a5a5] transition hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-test="producer-designLibrary-filters"
            data-balloon="Filter by type"
            data-balloon-pos="up"
            aria-label="Filter by type"
            @click="isFilterPanelOpen = !isFilterPanelOpen"
          >
            <span class="fak fa-bars-filter-regular"></span>
          </button>

          <div
            v-if="isFilterPanelOpen"
            class="design-library__filter-panel"
            data-test="producer-designLibrary-filtersPanel"
          >
            <ul
              :id="`listbox-${_uid}`"
              class="design-library__optionList"
              role="listbox"
              aria-multiselectable="true"
              aria-label="Option list"
            >
              <li
                class="design-library__optionListItem"
                role="option"
                aria-label="image"
                :aria-selected="selected.includes('image')"
                :data-test="`producer-searchFilter-option:image`"
              >
                <div class="tb-checkbox">
                  <input
                    v-model="assetTypesModel"
                    value="image"
                    type="checkbox"
                    id="image-filter"
                    :data-test="`producer-searchFilter-checkBox:image`"
                  />

                  <label for="image-filter">Image</label>
                </div>
              </li>
              <li
                class="design-library__optionListItem"
                role="option"
                aria-label="video"
                :aria-selected="selected.includes('video')"
                :data-test="`producer-searchFilter-option:video`"
              >
                <div class="tb-checkbox">
                  <input
                    v-model="assetTypesModel"
                    value="video"
                    type="checkbox"
                    id="video-filter"
                    :data-test="`producer-searchFilter-checkBox:video`"
                  />

                  <label for="video-filter">Video</label>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <button
        class="border-0 bg-transparent p-0 text-[13px] font-medium text-[#a5a5a5] transition hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-test="producer-designLibrary-sort"
          data-balloon="Sort by title"
          data-balloon-pos="up"
          aria-label="Sort by title"
          title="Sort by title"
          @click="toggleAssetSortDir"
        >
          <span
            class="fa"
            :class="{
              'fa-sort-amount-up-alt': assetSortDir === 'asc',
              'fa-sort-amount-down': assetSortDir === 'desc',
            }"
          />
        </button>
      </div>

      <!-- Breadcrumbs -->
      <div
        v-if="assetDirectory"
        class="breadcrumbs mb-3 flex items-center gap-2 px-3"
      >
        <button
        class="border-0 bg-transparent p-0 text-[13px] font-medium text-white transition hover:text-[#2bacca] focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-test="producer-designLibrary-rootDirectory"
          @click="changeDirectory(null)"
        >
          Library
        </button>

        <template v-if="assetDirectory.parent">
          <span class="slash ml-1 mr-0">/</span>
          <button
            class="border-0 bg-transparent p-0 text-[13px] font-medium text-white transition hover:text-[#2bacca] focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-test="producer-designLibrary-rootDirectory"
            @click="changeDirectory(assetDirectory.parent._id)"
          >
            ...
          </button>
        </template>

        <span class="slash ml-1 mr-0">/</span>

        <span
          class="tagboard-title"
          :title="assetDirectory.title"
          :data-test="`producer-designLibrary-curatedContent-currentBoard:${assetDirectory._id}`"
        >
          {{ assetDirectory.title }}
        </span>
      </div>

      <div
        ref="sourceList"
        class="source-list h-full min-h-0"
        data-test="producer-designLibrary-displayElements-list"
        >
        <!-- Folders -->
      <button
        v-for="d in assetDirectories"
        :key="d._id"
        class="design-library__directory ellipsis flex items-center gap-2 text-left"
        @click="changeDirectory(d._id)"
      ><span class="far fa-folder" /> {{ d.title }}</button>

        <div v-if="isUploading"
          class="my-4 flex items-center justify-center"
          data-test="producer-designLibrary-uploading"
        >
          <div class="text-center">
            <p>Uploading..</p>
            <span class="fas fa-spinner-third fa-spin fa-xl"></span>
          </div>
        </div>

        <!-- Assets -->
        <draggable
          v-model="assets"
          :clone="clone"
          :sort="false"
          animation="200"
          :group="{ name: 'timeline', pull: 'clone', put: false }"
          revert-on-spill="true"
          class="assets mt-2"
          @start="startDrag"
          @end="$store.dispatch('clearDragType')"
        >
          <div
            v-for="(a, idx) in assets"
            :key="a._id"
            :data-asset-id="a._id"
            class="assets__asset relative rounded-[3px] border border-[#3a3a3a] p-[5px] transition-[border-color,box-shadow,transform] duration-150 ease-in-out"
            :class="{ 'assets__asset--selected': selected.includes(a) }"
          >
            <input
              type="checkbox"
              :id="a._id"
              class="assets__checkbox"
              :value="a"
              v-model="selected"
              :data-test="`producer-designLibrary-asset:${a._id}`"
            >
            <label
              class="assets__checkbox-label"
              :for="a._id"
              @click.exact="lastSelectedAssetIndex = idx"
              @click.shift.stop.prevent="selectRange(idx)"
            />

            <img
              :src="getAssetImageUrl(a)"
              :alt="a.title"
              class="assets__thumbnail aspect-[16/9] w-full max-w-full rounded-[3px] bg-[#d9d9d9] object-contain"
            />
            <div class="assets__info flex items-center justify-between mt-1">
              <p class="m-0 ellipsis">{{ a.title }}</p>
              <button
                class="assets__preview-btn relative z-[1] border-0 bg-transparent text-[#a5a5a5]"
                @click="openAssetPreview(a)"
              ><span class="fa fa-search"/></button>
            </div>
          </div>
        </draggable>

        <div ref="loadTrigger" class="load-trigger">
          <div v-if="isLoading" class="loading loading--inline mt-2" data-test="producer-designLibrary-loading">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="producer-assets-selected flex items-center justify-end p-4"
      data-test="producer-designLibrary-selected"
    >
      <p class="producer-assets-selected__count mb-0 mr-4 mt-0 text-primary" data-test="producer-designLibrary-selected-count">{{ selected.length }} selected</p>

      <button
        class="ml-4 rounded-[6px] border-0 bg-[#2bacca] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#23cdf4] disabled:cursor-not-allowed disabled:opacity-60"
        :class="{'disabled': !selected.length }"
        :disabled="!selected.length"
        data-test="producer-designLibrary-selected-addSelectedToPlaylist"
        @click="addSelectedToPlaylist('assets')"
      >
        Add to Playlist
      </button>
    </div>

    <!-- Preview Modal -->
    <modal
      v-if="isPreviewModalOpen"
      class="modal--large preview-modal"
      @close="closePreviewModal"
    >
      <template #header>
        <div class="title">Asset Preview</div>

        <button
          class="modal-close border-0 bg-transparent p-0 text-[13px] font-medium text-[#a5a5a5] transition hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-test="producer-designLibrary-closePreview"
          @click="closePreviewModal"
        >
          <span class="fas fa-times" />
        </button>
      </template>

      <img
        v-if="previewAsset.asset_type === 'image'"
        :src="previewAsset.media_url"
        :alt="previewAsset.title"
        class="aspect-[16/9] w-full rounded-[3px] object-contain"
        style="background-position: 0 0, 0.625rem 0.625rem; background-size: 1.25rem 1.25rem; background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%), linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%); background-blend-mode: normal, difference, normal;"
      />

      <video
        v-else-if="previewAsset.asset_type === 'video'"
        :src="previewAsset.media_url"
        :alt="previewAsset.title"
        class="aspect-[16/9] w-full rounded-[3px] object-contain"
        style="background-position: 0 0, 0.625rem 0.625rem; background-size: 1.25rem 1.25rem; background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%), linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%); background-blend-mode: normal, difference, normal;"
        controls
      ></video>

      <template #footer>
        <button
          class="rounded-[6px] border-0 bg-[#2bacca] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#23cdf4]"
          data-test="producer-designLibrary-addToPlaylist"
          @click="addToPlaylist(previewAsset, 'asset'); closePreviewModal();"
        >
          Add to Playlist
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { pick } from 'lodash-es';
import * as uuid from 'uuid';

import { getPresignedUrl, createAsset } from '@/js/services/assets';

import tabMixins from '@/js/mixins/tab-mixins';
import Modal from '@/js/components/modal/Modal.vue';
import SearchFilter from '@/js/shared/SearchFilter.vue';

export default {
  name: 'DesignLibrary',
  mixins: [tabMixins],
  components: {
    Modal,
    SearchFilter,
  },
  data() {
    return {
      isPreviewModalOpen: false,
      previewAsset: null,
      isFilterPanelOpen: false,
      lastSelectedAssetIndex: null,
      isDragging: false,
      isUploading: false,
    };
  },
  computed: {
    ...mapState('assets', [
      'assets',
      'assetDirectory',
      'assetDirectories',
      'assetSearch',
      'assetSortDir',
      'assetTypes',
    ]),
    ...mapGetters('assets', [
      'isLoading',
      'hasMoreAssets',
    ]),
    ...mapGetters([
      'optimizeImages',
    ]),

    ownerId() {
      return this.$store.state.production.production.owner;
    },

    assetTypesModel: {
      get() {
        return this.assetTypes;
      },
      set(v) {
        this.setAssetTypes(v);
      },
    },
  },
  watch: {
    assets() {
      if (!this.$refs.sourceList || !this.assets.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },
  methods: {
    ...mapActions('assets', [
      'getAssets',
      'setDirectory',
      'searchAssets',
      'toggleAssetSortDir',
      'setAssetTypes',
      'getNextPage',
      'addUploadedAsset',
    ]),

    changeDirectory(id) {
      this.setDirectory(id);
    },
    openAssetPreview(asset) {
      this.previewAsset = asset;
      this.isPreviewModalOpen = true;
    },
    closePreviewModal() {
      this.isPreviewModalOpen = false;
      this.previewAsset = null;
    },
    getAssetImageUrl(asset) {
      const url = asset.thumbnail_url || asset.media_url;

      if (!url || asset.asset_type !== 'image') {
        return url;
      }

      try {
        const parsedUrl = new URL(url, window.location.origin);
        parsedUrl.searchParams.set('w', '200');

        return parsedUrl.href;
      } catch (error) {
        return url;
      }
    },
    clone(original) {
      const clone = pick(original, ['_id', 'title', 'media_url', 'thumbnail_url', 'asset_type']);
      clone.layout = 'asset';
      clone.timeline_id = uuid.v1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.posts = []; // needed to pass timeline item validation
      clone.fields = {};
      clone.mediaMeta = this.optimizeImages ? { optimize: true } : {};

      return clone;
    },
    startDrag(e) {
      const { item } = e;
      const { assetId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === assetId);

      this.$store.dispatch('setDragType', {
        type: 'asset',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },
    selectRange(assetIdx) {
      if (this.lastSelectedAssetIndex === null) {
        this.lastSelectedAssetIndex = assetIdx;
      }

      const start = Math.min(this.lastSelectedAssetIndex, assetIdx);
      const end = Math.max(this.lastSelectedAssetIndex, assetIdx);

      this.selected = this.assets.slice(start, end + 1);
    },

    startDragover(e) {
      if (e.dataTransfer.types.includes('Files')) {
        this.isDragging = true;
      }
    },

    async handleDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      const currentDirectory = this.assetDirectory?._id;

      // file utilities (lifted from tgb-app)
      const getUniqueFilename = (filename) => {
        const formattedFilename = filename.replace(/\s/g, '_');
        const [, name, extension] = /(?:(.*)\.([^.]+))?$/.exec(formattedFilename);
        const hex = Array.from({ length: 5 }).map(() => Math.floor(Math.random() * 16).toString(16)).join('');
        return `${name}-${hex}.${extension}`;
      };

      const getFileExtension = (filename) => /(?:\.([^.]+))?$/.exec(filename)[1];

      const droppedFiles = Array.from(e.dataTransfer.files);

      const validFiles = droppedFiles.filter((file) => {
        if (![ 'image', 'video' ].includes(file.type.split('/')[0])) {
          this.errorToast(`${file.name} is not a valid image or video file`);
          return false;
        }

        return true;
      });

      this.isUploading = true;
      await Promise.allSettled(validFiles.map(async (file) => {
        const signedUrlParams = {
          filename: getUniqueFilename(file.name),
          contentType: file.type,
          type: file.type.split('/')[0],
        };

        try {
          // Get data for signed url
          const { url, fields } = await getPresignedUrl(signedUrlParams);

          const s3Response = await fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type,
            },
            body: file,
          });

          if (!s3Response.ok) {
            throw new Error(`Error uploading file ${file.name} to s3`);
          }

          // create asset
          const meta = {
            type: getFileExtension(file.name),
            size: file.size,
            content_type: file.type,
          };

          if (signedUrlParams.type === 'image') {
            meta.height = file.height;
            meta.width = file.width;
          }

          const newAsset = {
            owner: this.ownerId,
            title: file.name.replace(/\s/g, '_'),
            tags: [meta.type, signedUrlParams.type],
            meta,
            media_url: `${process.env.ASSET_MEDIA_URL}${fields.Key}`,
            s3_key: fields.Key,
            asset_type: signedUrlParams.type,
            directory: currentDirectory,
          };

          const newId = await createAsset(newAsset);
          newAsset._id = newId;

          this.addUploadedAsset(newAsset);

          return newAsset;
        } catch (error) {
          this.errorToast(`Error uploading ${file.name}`);
        }
      }));

      this.isUploading = false;
    },
    loadMore() {
      if (!this.isLoading && this.hasMoreAssets) {
        this.getNextPage();
      }
    },
  },
  created() {
    this.getAssets();
  },
  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },
  beforeUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },
};
</script>

<style lang="scss" scoped>
.design-library {
  padding: 0.625rem 1.25rem;
  flex: 1 0 0;

  &__directory {
    display: block;
    width: 100%;
    padding: 0.5rem 0.8125rem;
    text-align: left;
    font-size: 0.75rem;
    color: #fff;
    background-color: #414141;
    border: none;
    border-radius: 0.375rem;

    & .fa-folder {
      color: variables.$color-on-surface-medium;
    }

    & + & {
      margin-top: 0.625rem;
    }
  }

  &__filterWrap {
    position: relative;
    overflow: visible;
  }

  &__filter-panel {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 100;
    background-color: variables.$color-02dp;
    padding: 0.5rem;
    width: auto;
    max-width: 100%;
    max-height: 25rem;
    display: flex;
    flex-direction: column;
    border: 1px solid variables.$color-on-surface-medium;
    border-radius: 3px;
  }

  &__optionList {
    list-style: none;
    flex: 1;
    padding: 0;
    margin: 0;
    overflow-y: auto;
  }

  &__optionListItem {
    & + & {
      margin-top: 0.25rem;
    }
  }
}

.assets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 0.875rem;

  &__asset {
    border: 1px solid #3a3a3a;
    border-radius: 3px;
    padding: 0.3125rem;
    position: relative;
    transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;

    &--selected {
      box-shadow: 0 0 0 1px variables.$color-primary;
    }

    &:hover {
      border-color: #4a4a4a;
      box-shadow: 0 0.25rem 0.5rem rgba(255, 255, 255, .08);
      transform: translateY(-1px);
    }

    &.sortable-ghost {
      max-height: 3.125rem;
      margin: 0;
      padding: 0.625rem;
      background-color: variables.$color-06dp;
      border: none;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      & > :not(img) {
        display: none;
      }

      & > .assets__info {
        display: block !important;
        margin-left: 0.5rem;

        & > .assets__preview-btn {
          display: none;
        }
      }

      & > img {
        width: 4.375rem;
        object-fit: contain;
        background-color: transparent;
      }
    }
  }

  &__checkbox {
    position: absolute;
    margin: 0;
    opacity: 0;
  }

  &__checkbox:checked + &__checkbox-label::before {
    content: "\f00c"; // fa-check
    background-color: variables.$color-primary;
  }

  &__checkbox-label {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &::before {
      content: '';
      font-family: 'Font Awesome 5 Pro';
      font-weight: 300;
      color: black;
      font-size: 0.625rem;
      position: absolute;
      top: 0.4375rem;
      left: 0.4375rem;
      display: inline-block;
      width: 0.75rem;
      height: 0.75rem;
      background-color: #fff;
      border-radius: 3px;
      cursor: pointer;
      text-align: center;
      vertical-align: middle;
      line-height: 0.75rem;
    }
  }

  &__thumbnail {
    width: 100%;
    max-width: 100%;
    object-fit: contain;
    aspect-ratio: 16/9;
    background-color: #d9d9d9;
    border-radius: 3px;
  }

  &__preview-btn {
    background-color: transparent;
    border: none;
    color: variables.$color-on-surface-medium;
    z-index: 1;
  }
}

.file-dropzone {
  position: relative;
}

</style>
